import React, { useEffect, useState } from "react";
import { Modal, Input, Select } from "antd";
import ProjectSelect from "../../atoms/ProjectSelect/ProjectSelect";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import {
  BOARD_BACKGROUND_PRESETS,
  BOARD_VISIBILITY_OPTIONS,
} from "../../../app/utils/projectBoard/boardDesignTokens";
import { createBoardApi, updateBoardApi } from "../../../app/utils/projectBoard/projectBoardApi";
import {
  formatTicketPrefixPreview,
  sanitizeTicketPrefixInput,
  validateTicketPrefixInput,
} from "../../../app/utils/projectBoard/boardTicketPrefix";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import "./BoardCreateModal.scss";

const resolveBackgroundId = (color) => {
  const match = BOARD_BACKGROUND_PRESETS.find(
    (bg) => String(bg.color).toLowerCase() === String(color || "").toLowerCase()
  );
  return match?.id || BOARD_BACKGROUND_PRESETS[0].id;
};

const BoardCreateModal = ({ open, onClose, onCreated, onUpdated, board = null }) => {
  const isEdit = Boolean(board?.boardId || board?.board_id);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ticketPrefix, setTicketPrefix] = useState("");
  const [project, setProject] = useState(null);
  const [visibility, setVisibility] = useState("team");
  const [memberIds, setMemberIds] = useState([]);
  const [backgroundId, setBackgroundId] = useState(BOARD_BACKGROUND_PRESETS[0].id);
  const [submitting, setSubmitting] = useState(false);
  const [projectError, setProjectError] = useState(false);

  const resetForm = () => {
    setName("");
    setDescription("");
    setTicketPrefix("");
    setProject(null);
    setVisibility("team");
    setMemberIds([]);
    setBackgroundId(BOARD_BACKGROUND_PRESETS[0].id);
    setProjectError(false);
  };

  useEffect(() => {
    if (!open) return;
    if (!board) {
      resetForm();
      return;
    }
    setName(board.name || "");
    setDescription(board.description || "");
    setTicketPrefix(board.ticketPrefix || "");
    setVisibility(board.visibility || "team");
    setMemberIds(
      (board.memberIds || [])
        .map(Number)
        .filter((id) => id > 0 && id !== Number(board.ownerId))
    );
    setBackgroundId(resolveBackgroundId(board.backgroundColor));
    setProject(
      board.projectId
        ? {
            value: board.projectId,
            label: board.projectName || `Project ${board.projectId}`,
          }
        : null
    );
    setProjectError(false);
  }, [open, board]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      showErrorToast("Board name is required.");
      return;
    }

    if (!isEdit) {
      if (!project?.value) {
        setProjectError(true);
        showErrorToast("Please select a project.");
        return;
      }

      const ticketError = validateTicketPrefixInput(ticketPrefix);
      if (ticketError) {
        showErrorToast(ticketError);
        return;
      }
    }

    const bg = BOARD_BACKGROUND_PRESETS.find((b) => b.id === backgroundId);
    setSubmitting(true);

    try {
      if (isEdit) {
        const boardId = board.boardId || board.board_id;
        await updateBoardApi(boardId, {
          name: name.trim(),
          description: description.trim(),
          backgroundColor: bg?.color || "#0079BF",
          visibility,
          memberIds: memberIds.map(Number).filter((id) => id > 0),
        });
        showSuccessToast("Board updated successfully.");
        onUpdated?.(boardId);
        onClose?.();
        return;
      }

      const result = await createBoardApi({
        projectId: project.value,
        name: name.trim(),
        description: description.trim(),
        ticketPrefix: ticketPrefix.trim(),
        backgroundColor: bg?.color || "#0079BF",
        visibility,
        memberIds: memberIds.map(Number).filter((id) => id > 0),
      });
      showSuccessToast("Board created successfully.");
      onCreated?.(result?.boardId);
      resetForm();
      onClose?.();
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message
        || error?.message
        || (isEdit ? "Failed to update board." : "Failed to create board.");
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Board" : "Create Board"}
      open={open}
      onCancel={() => {
        if (!isEdit) resetForm();
        onClose?.();
      }}
      onOk={handleSubmit}
      okText={isEdit ? "Save" : "Create"}
      confirmLoading={submitting}
      destroyOnClose
    >
      <div className="board-create-modal">
        <label className="board-create-modal__label">Linked project</label>
        <ProjectSelect
          value={project}
          onChange={(val) => {
            setProject(val);
            setProjectError(false);
          }}
          showError={projectError}
          isDisabled={isEdit}
        />

        <label className="board-create-modal__label">Board name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sprint 14"
          maxLength={200}
        />

        {!isEdit && (
          <>
            <label className="board-create-modal__label">Ticket prefix</label>
            <Input
              value={ticketPrefix}
              onChange={(e) => setTicketPrefix(sanitizeTicketPrefixInput(e.target.value))}
              placeholder="e.g. T or TCS"
              maxLength={3}
            />
            {formatTicketPrefixPreview(ticketPrefix) && (
              <p className="board-create-modal__hint">
                First card ticket: <strong>{formatTicketPrefixPreview(ticketPrefix)}</strong>
              </p>
            )}
          </>
        )}

        {isEdit && ticketPrefix && (
          <p className="board-create-modal__hint">
            Ticket prefix: <strong>{String(ticketPrefix).toUpperCase()}</strong> (cannot be changed)
          </p>
        )}

        <label className="board-create-modal__label">Description (optional)</label>
        <Input.TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={2000}
        />

        <label className="board-create-modal__label">Visibility</label>
        <Select
          value={visibility}
          onChange={setVisibility}
          style={{ width: "100%" }}
          options={BOARD_VISIBILITY_OPTIONS.map((o) => ({
            value: o.value,
            label: `${o.label} — ${o.description}`,
          }))}
        />

        <label className="board-create-modal__label">Board members (optional)</label>
        <EmployeeSelect
          isMultiple
          value={memberIds}
          onSelectEmployee={(ids) =>
            setMemberIds(Array.isArray(ids) ? ids : ids != null ? [ids] : [])
          }
          placeholder="Add board members..."
          width="100%"
          excludeCeoCmo={false}
        />

        <label className="board-create-modal__label">Background</label>
        <div className="board-create-modal__colors">
          {BOARD_BACKGROUND_PRESETS.map((bg) => (
            <button
              key={bg.id}
              type="button"
              className={`board-create-modal__swatch ${backgroundId === bg.id ? "board-create-modal__swatch--active" : ""}`}
              style={{ backgroundColor: bg.color }}
              title={bg.name}
              onClick={() => setBackgroundId(bg.id)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default BoardCreateModal;
